/**
 * TanStack Query hooks for the Events workspace. Every key starts with
 * "events" so main.tsx can keep them out of the localStorage persister:
 * descriptions carry customers' emails and amounts.
 */
import { useEffect, useMemo, useRef, useState } from "react"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  eventsApi,
  type EventEntry,
  type FeedPage,
  type FeedParams,
  type PrefChange,
  type SeriesParams,
} from "@/services/api/events"
import { mergeEvents, prependFresh, unseenIds, type FeedFilters } from "@/lib/events"

const NO_PERSIST = { persist: false } as const

/** Is the tab visible? The live poll only runs while it is. */
function useDocumentVisible() {
  const [visible, setVisible] = useState(() => typeof document === "undefined" || document.visibilityState === "visible")
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState === "visible")
    document.addEventListener("visibilitychange", onChange)
    return () => document.removeEventListener("visibilitychange", onChange)
  }, [])
  return visible
}

export function useEventProjects() {
  return useQuery({ queryKey: ["events", "projects"], queryFn: () => eventsApi.projects(), meta: NO_PERSIST, staleTime: 30_000 })
}

export function useEventProject(project: string | null) {
  return useQuery({
    queryKey: ["events", "project", project],
    queryFn: () => eventsApi.project(project!),
    enabled: !!project,
    meta: NO_PERSIST,
    staleTime: 15_000,
  })
}

function filterParams(f: FeedFilters): FeedParams {
  return { channel: f.channel || undefined, q: f.q || undefined, user: f.user || undefined, tag: f.tags.length ? f.tags : undefined }
}

/**
 * A feed (all projects, one project, or one sender user's journey): older
 * pages by `before`, plus a 5-second poll by `after` while the tab is
 * visible. Returns events newest first and the ids that arrived live, so
 * the list can animate just those in.
 */
export function useEventFeed(scope: { project: string | null; userId?: string }, filters: FeedFilters) {
  const visible = useDocumentVisible()
  const params = useMemo(() => filterParams(filters), [filters])
  const key = ["events", "feed", scope.project ?? "all", scope.userId ?? "", params] as const

  const fetchPage = (extra: FeedParams): Promise<FeedPage> =>
    scope.userId && scope.project
      ? eventsApi.userEvents(scope.project, scope.userId, { ...params, ...extra })
      : eventsApi.feed(scope.project, { ...params, ...extra })

  const pages = useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam }) => fetchPage(pageParam ? { before: pageParam } : {}),
    initialPageParam: "" as string,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    meta: NO_PERSIST,
    staleTime: 0,
  })

  // Live events and the cursor they continue from reset whenever the feed changes.
  const keyString = JSON.stringify(key)
  const [live, setLive] = useState<{ key: string; events: EventEntry[]; cursor: string | null; freshIds: Set<string> }>({
    key: keyString,
    events: [],
    cursor: null,
    freshIds: new Set(),
  })
  const current = live.key === keyString ? live : { key: keyString, events: [], cursor: null, freshIds: new Set<string>() }
  const firstNewest = pages.data?.pages[0]?.newest_cursor ?? null
  const cursor = current.cursor ?? firstNewest

  const poll = useQuery({
    queryKey: ["events", "live", keyString, cursor],
    queryFn: () => fetchPage({ after: cursor! }),
    enabled: !!cursor && visible && pages.isSuccess,
    refetchInterval: visible ? 5000 : false,
    meta: NO_PERSIST,
    gcTime: 10_000,
  })

  const events = useMemo(
    () => mergeEvents(current.events, pages.data?.pages.flatMap((p) => p.data) ?? []),
    [current.events, pages.data],
  )
  // Every id on screen, so a poll's overlap window (the API re-returns up to
  // ten minutes before the cursor) never re-animates what is already shown.
  const shownIds = useRef<Set<string>>(new Set())
  shownIds.current = new Set(events.map((e) => e.id))

  useEffect(() => {
    const data = poll.data
    if (!data) return
    setLive((prev) => {
      const base = prev.key === keyString ? prev : { key: keyString, events: [], cursor: null, freshIds: new Set<string>() }
      const nextCursor = data.newest_cursor ?? base.cursor ?? cursor
      const unseen = unseenIds(shownIds.current, data.data)
      if (unseen.length === 0 && nextCursor === base.cursor) return base
      const freshIds = new Set(base.freshIds)
      for (const id of unseen) freshIds.add(id)
      return { key: keyString, events: prependFresh(base.events, data.data), cursor: nextCursor, freshIds }
    })
    // cursor is read only as a fallback; the poll result is what changes state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poll.data, keyString])

  return {
    events,
    freshIds: current.freshIds,
    isLoading: pages.isLoading,
    isError: pages.isError,
    error: pages.error,
    hasMore: !!pages.hasNextPage,
    loadMore: () => pages.fetchNextPage(),
    loadingMore: pages.isFetchingNextPage,
    live: visible && !!cursor,
  }
}

export function useEventEntry(ulid: string | undefined) {
  return useQuery({
    queryKey: ["events", "entry", ulid],
    queryFn: () => eventsApi.entry(ulid!),
    enabled: !!ulid,
    meta: NO_PERSIST,
    retry: false,
  })
}

export function useEventInsights(project: string | null) {
  return useQuery({
    queryKey: ["events", "insights", project],
    queryFn: () => eventsApi.insights(project!),
    enabled: !!project,
    meta: NO_PERSIST,
    refetchInterval: 30_000,
  })
}

/** The Events map: located events grouped into points, for one project or all. */
export function useEventMap(project: string | null, days: number) {
  return useQuery({
    queryKey: ["events", "map", project, days],
    queryFn: () => eventsApi.map(project, days),
    meta: NO_PERSIST,
    refetchInterval: 60_000,
  })
}

export function useEventTypes(project: string | null) {
  return useQuery({
    queryKey: ["events", "event-types", project],
    queryFn: () => eventsApi.eventTypes(project!),
    enabled: !!project,
    meta: NO_PERSIST,
    staleTime: 60_000,
  })
}

export function useEventTags(project: string | null) {
  return useQuery({
    queryKey: ["events", "tags", project],
    queryFn: () => eventsApi.tags(project!),
    enabled: !!project,
    meta: NO_PERSIST,
    staleTime: 60_000,
  })
}

export function useEventSeries(project: string | null, params: SeriesParams, enabled = true) {
  return useQuery({
    queryKey: ["events", "series", project, params],
    queryFn: () => eventsApi.series(project!, params),
    enabled: !!project && enabled,
    meta: NO_PERSIST,
    staleTime: 60_000,
  })
}

export function useSavedCharts(project: string | null) {
  return useQuery({
    queryKey: ["events", "charts", project],
    queryFn: () => eventsApi.charts(project!),
    enabled: !!project,
    meta: NO_PERSIST,
  })
}

export function useEventUsers(project: string | null, q: string, page: number) {
  return useQuery({
    queryKey: ["events", "users", project, q, page],
    queryFn: () => eventsApi.users(project!, { q: q || undefined, page, limit: 50 }),
    enabled: !!project,
    meta: NO_PERSIST,
    placeholderData: (prev) => prev,
  })
}

export function useEventUser(project: string | null, id: string | undefined) {
  return useQuery({
    queryKey: ["events", "user", project, id],
    queryFn: () => eventsApi.user(project!, id!),
    enabled: !!project && !!id,
    meta: NO_PERSIST,
    retry: false,
  })
}

export function useEventTokens(project: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["events", "tokens", project],
    queryFn: () => eventsApi.tokens(project!),
    enabled: !!project && enabled,
    meta: NO_PERSIST,
  })
}

export function useNotificationPrefs() {
  return useQuery({ queryKey: ["events", "prefs"], queryFn: () => eventsApi.prefs(), meta: NO_PERSIST })
}

export function useSavePrefs() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (prefs: PrefChange[]) => eventsApi.savePrefs(prefs),
    onSuccess: (data) => qc.setQueryData(["events", "prefs"], data),
  })
}

export function usePushDevices() {
  return useQuery({ queryKey: ["events", "devices"], queryFn: () => eventsApi.devices(), meta: NO_PERSIST })
}

/** Invalidate everything Events after an admin change. */
export function useInvalidateEvents() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: ["events"] })
}
